import {
  Building2,
  CreditCard,
  Mail,
  Receipt,
  Settings2,
  Shield,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Link, matchPath, useHistory, useLocation } from "react-router-dom";

import {
  getActiveOrganisation,
  useIdentity,
} from "contexts/identity-context";
import * as Routes from "routes";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SettingsLink {
  label: string;
  url: string;
  icon: LucideIcon;
  isActive?: (pathname: string) => boolean;
}

const accountLinks: SettingsLink[] = [
  { label: "General", url: Routes.userGeneralSettings, icon: Settings2 },
  { label: "Security", url: Routes.userSecurity, icon: Shield },
  {
    label: "Pending Invitations",
    url: Routes.userPendingInvitations,
    icon: Mail,
  },
];

const organisationLinks: SettingsLink[] = [
  { label: "General", url: Routes.orgGeneralSettings, icon: Building2 },
  {
    label: "Members",
    url: Routes.orgMembers,
    icon: Users,
    isActive: (pathname) =>
      pathname === Routes.orgMembers ||
      Boolean(matchPath(pathname, { path: Routes.orgMemberDetails, exact: true })),
  },
  { label: "Subscription", url: Routes.orgSubscription, icon: CreditCard },
  { label: "Billing", url: Routes.orgBillingDetails, icon: Receipt },
];

function isSettingsLinkActive(link: SettingsLink, pathname: string) {
  return (
    link.isActive?.(pathname) ??
    (pathname === link.url || pathname.startsWith(link.url + "/"))
  );
}

function SettingsLinkGroup({
  label,
  links,
  pathname,
}: {
  label: string;
  links: SettingsLink[];
  pathname: string;
}) {
  return (
    <SidebarGroup className="px-0">
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarMenu>
        {links.map((link) => (
          <SidebarMenuItem key={link.url}>
            <SidebarMenuButton
              asChild
              isActive={isSettingsLinkActive(link, pathname)}
            >
              <Link to={link.url}>
                <link.icon />
                <span>{link.label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

export function SettingsSidebar() {
  const pathname = useLocation().pathname;
  const [identity] = useIdentity();
  const hasOrganisation = Boolean(getActiveOrganisation(identity));

  return (
    <nav aria-label="Settings navigation">
      <SettingsLinkGroup
        label="User"
        links={accountLinks}
        pathname={pathname}
      />
      {hasOrganisation && (
        <SettingsLinkGroup
          label="Organisation"
          links={organisationLinks}
          pathname={pathname}
        />
      )}
    </nav>
  );
}

export function SettingsMobileNav() {
  const pathname = useLocation().pathname;
  const history = useHistory();
  const [identity] = useIdentity();
  const hasOrganisation = Boolean(getActiveOrganisation(identity));
  const links = hasOrganisation
    ? [...accountLinks, ...organisationLinks]
    : accountLinks;
  const activeLink = links.find((link) =>
    isSettingsLinkActive(link, pathname),
  );
  const isNestedPage = Boolean(activeLink && pathname !== activeLink.url);

  return (
    <div className="space-y-2 md:hidden">
      <label
        htmlFor="settings-mobile-navigation"
        className="text-sm font-medium"
      >
        Settings section
      </label>
      <Select
        value={isNestedPage ? "" : activeLink?.url ?? Routes.userGeneralSettings}
        onValueChange={(url) => history.push(url)}
      >
        <SelectTrigger id="settings-mobile-navigation">
          <SelectValue placeholder={activeLink?.label ?? "General"} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>User</SelectLabel>
            {accountLinks.map((link) => (
              <SelectItem key={link.url} value={link.url}>
                {link.label}
              </SelectItem>
            ))}
          </SelectGroup>
          {hasOrganisation && (
            <SelectGroup>
              <SelectLabel>Organisation</SelectLabel>
              {organisationLinks.map((link) => (
                <SelectItem key={link.url} value={link.url}>
                  {link.label}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
